import { gql } from '@apollo/client'
import { isNullish } from '@apollo/client/cache/inmemory/helpers'
import { ReplyProps, VisitCategory } from '@place/ui'
import dayjs from 'dayjs'

import { isEmptyArray } from '@local/utils'

import {
  ExtractCoverMedia_MediaItemsFragment,
  OwnerReply_ReviewGroupFragment,
  PlaceOtherReview_ReviewGroupFragment,
  ReviewContent_ReviewGroupFragment,
  ReviewFeed_ReviewContent_ReviewGroupFragment,
  VisitKeywords_ReviewGroupFragment,
} from '@web/gql/graphql'
import { getCurrentLocale } from '@web/libs/translate/translate.state'
import { ArrayElement } from '@web/types'

import { extractByTypename } from './apollo/typeGuard'

dayjs.locale('ko')

export const extractCoverMedia = (mediaItems: ExtractCoverMedia_MediaItemsFragment[]) => {
  const coverMedia = mediaItems.find(mediaItem => mediaItem.isCover) ?? mediaItems[0]
  if (!coverMedia) return undefined

  const { ImageItem, VideoItem } = extractByTypename(coverMedia)

  if (ImageItem) return ImageItem.url

  if (VideoItem) return VideoItem.thumbnail

  return undefined
}

extractCoverMedia.fragment = gql`
  fragment ExtractCoverMedia_MediaItems on MediaItem {
    isCover
    ... on ImageItem {
      url
    }
    ... on VideoItem {
      thumbnail
    }
  }
`

type Keyword = ArrayElement<VisitKeywords_ReviewGroupFragment['visitKeywords']>
export const getDisplayKeyword = (keyword: Keyword) => {
  const locale = getCurrentLocale()
  return keyword.displayText?.[locale] ?? keyword.text[locale]
}

export const convertVisitKeywords = (reviewGroup: VisitKeywords_ReviewGroupFragment): VisitCategory[] | undefined => {
  const visitCategories = reviewGroup?.place?.visitCategories
  const visitKeywords = reviewGroup?.visitKeywords

  if (isNullish(visitCategories) || isEmptyArray(visitCategories)) return undefined

  if (isNullish(visitKeywords) || isEmptyArray(visitKeywords)) return undefined

  const keywords = visitCategories.reduce<VisitCategory[]>((prev, visitCategory) => {
    const visitCategoryKeywords = visitCategory.keywords.map(({ code }) => code)
    const selectedKeywords = visitKeywords.filter(visitKeyword => visitCategoryKeywords.includes(visitKeyword.code))

    if (!visitCategory.subTitle.korean) return prev

    if (isEmptyArray(selectedKeywords)) return prev

    /**
     * '대기 시간'만 label 노출합니다 (참고: https://oss.navercorp.com/place/local/issues/17083)
     * '대기 시간' 노출 스펙 삭제 (참고: https://oss.navercorp.com/place/local/issues/19799#issuecomment-15552299)
     */
    return prev.concat({
      code: visitCategory.code,
      name: undefined,
      keywords: selectedKeywords.map(k => ({ code: k.code, name: getDisplayKeyword(k) ?? '' })),
    })
  }, [])

  if (isEmptyArray(keywords)) return undefined

  return keywords
}

export const convertReply = (reviewGroup: OwnerReply_ReviewGroupFragment): ReplyProps | undefined => {
  const { reply } = reviewGroup
  if (!reply?.isQualified) return undefined

  const replyDateTime = reply.updatedDateTime ?? reply.createdDateTime

  const isThisYear = dayjs().year() === dayjs(replyDateTime).year()
  const replyDate = isThisYear ? dayjs(replyDateTime).format('M.D.dd') : dayjs(replyDateTime).format('YYYY. M. D')

  return {
    replierName: reply.author,
    date: replyDate,
    text: reply.text,
    status: reply.isSuspended ? (reply.isTemporary ? 'TEMPORARILY_SUSPENDED' : 'PERMANENTLY_SUSPENDED') : 'ACTIVE',
  }
}

type ReviewGroupType =
  ReviewFeed_ReviewContent_ReviewGroupFragment | ReviewContent_ReviewGroupFragment | PlaceOtherReview_ReviewGroupFragment

export const isRestaurantReview = (reviewGroup: ReviewGroupType) => /(220036)/.test(
  reviewGroup.place?.categoryIdPath ?? '',
)
